// src/components/Dealer/DealerStatusChangeForm.tsx

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { toast } from 'sonner';
import { dealerApi } from '../../services/dealerApi';

const MEMO_MAX = 500;

interface DealerStatusChangeFormProps {
  dealerId: number;
  dealerName: string;
  mode: 'suspend' | 'unsuspend';
  /** 종사원증 확인 대기가 남아 있으면 이용정지 해제 때 '확인 중'으로 돌아간다 */
  hasPendingLicense: boolean;
  onDone: () => void;
  onCancel: () => void;
}

/**
 * 이용정지·해제 입력 (딜러 상세 상태 패널의 창, 신고 관리 딜러 요약 팝업에서 같이 쓴다).
 * 이용정지는 관리자 메모 필수 — 딜러에게는 보이지 않는다.
 */
export default function DealerStatusChangeForm({
  dealerId,
  dealerName,
  mode,
  hasPendingLicense,
  onDone,
  onCancel,
}: DealerStatusChangeFormProps) {
  const queryClient = useQueryClient();
  const [memo, setMemo] = useState('');
  const suspend = mode === 'suspend';
  const memoId = `${mode}-memo-${dealerId}`;

  const statusMutation = useMutation({
    mutationFn: (payload: { status: 'active' | 'suspended'; memo: string }) =>
      dealerApi.updateDealerStatus(dealerId, payload.status, payload.memo),
    onSuccess: (res) => {
      toast.success(res.message);
      onDone();
    },
    onError: (error: unknown) => {
      const message = error instanceof AxiosError ? error.response?.data?.message : null;
      toast.error(message || '상태를 바꾸지 못했습니다.');
    },
    onSettled: () => {
      // 상태가 보이는 곳을 모두 다시 불러온다 (이미 바뀐 경우 등 실패해도)
      queryClient.invalidateQueries({ queryKey: ['dealer', dealerId.toString()] });
      queryClient.invalidateQueries({ queryKey: ['dealer-status-history', dealerId] });
      queryClient.invalidateQueries({ queryKey: ['dealer-summary', dealerId] });
      queryClient.invalidateQueries({ queryKey: ['dealers'] });
    },
  });

  return (
    <div>
      <h3 className='mb-3 text-lg font-semibold text-gray-900 dark:text-white'>
        {suspend ? `${dealerName} 딜러를 이용정지할까요?` : '이용정지를 해제할까요?'}
      </h3>
      {suspend ? (
        <ul className='mb-4 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-400'>
          <li>딜러 앱이 바로 막히고 '이용이 제한된 계정입니다. 고객센터로 문의해 주세요.'만 보입니다.</li>
          <li>고객이 받은 인증링크와 공개 프로필도 '현재 이용할 수 없는 딜러입니다.'로 바뀝니다.</li>
        </ul>
      ) : (
        <p className='mb-4 text-sm text-gray-600 dark:text-gray-400'>
          {hasPendingLicense
            ? "종사원증 확인 대기가 남아 있어 '확인 중'으로 돌아갑니다. 딜러 앱과 고객 화면이 다시 열립니다."
            : "'활성'으로 돌아가고 딜러 앱과 고객 화면이 다시 열립니다."}
        </p>
      )}
      <label htmlFor={memoId} className='mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300'>
        {suspend ? '관리자 메모 (필수)' : '관리자 메모 (선택)'}
      </label>
      <textarea
        id={memoId}
        value={memo}
        onChange={(e) => setMemo(e.target.value.slice(0, MEMO_MAX))}
        rows={suspend ? 4 : 3}
        maxLength={MEMO_MAX}
        placeholder={suspend ? '예: 허위 매물 신고 3건 (10/5 접수)' : '예: 소명 확인, 신고 철회'}
        className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90'
      />
      {suspend && (
        <div className='mt-1 flex justify-between text-xs text-gray-500 dark:text-gray-400'>
          <span>딜러에게는 보이지 않습니다.</span>
          <span>
            {memo.length} / {MEMO_MAX}
          </span>
        </div>
      )}
      <div className='mt-5 flex justify-end gap-2'>
        <button
          type='button'
          onClick={onCancel}
          className='rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'>
          취소
        </button>
        {suspend ? (
          <button
            type='button'
            disabled={memo.trim() === '' || statusMutation.isPending}
            onClick={() => statusMutation.mutate({ status: 'suspended', memo: memo.trim() })}
            className='rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300'>
            {statusMutation.isPending ? '처리 중…' : '이용정지'}
          </button>
        ) : (
          <button
            type='button'
            disabled={statusMutation.isPending}
            onClick={() => statusMutation.mutate({ status: 'active', memo: memo.trim() })}
            className='rounded-lg bg-brand-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50'>
            {statusMutation.isPending ? '처리 중…' : '해제'}
          </button>
        )}
      </div>
    </div>
  );
}
